import React from 'react';
import { Box, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Bar, CartesianGrid, ComposedChart, LabelList, Legend, Line, ResponsiveContainer, Tooltip,
  XAxis, YAxis,
} from 'recharts';

import { formatLitre, formatNumber, formatTonne } from '../../../shared/utils/number';
import { MONO_FONT } from '../../../theme/muiTheme';

const compactNumber = (value) => (
  Math.abs(value) >= 1000
    ? `${formatNumber(value / 1000, { max: 1 })}k`
    : formatNumber(value, { max: Math.abs(value) < 20 ? 1 : 0 })
);

const compactVolume = (value) => (
  Math.abs(value) >= 1000
    ? `${formatNumber(value / 1000, { max: 1 })}k`
    : formatNumber(value, { max: Math.abs(value) < 20 ? 1 : 0 })
);

const formatEmissionLabel = (value) => formatTonne(value);

function CombinedTooltip({ active, label, payload }) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const litres = payload.find((item) => item.dataKey === 'volumeLitres');
  const emissions = payload.find((item) => item.dataKey === 'emissions');

  return (
    <Paper elevation={3} sx={{ minWidth: 190, p: 1.25 }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 800 }}>{label}</Typography>
      <Stack spacing={0.65} sx={{ mt: 0.75 }}>
        <Typography variant="caption" sx={{ fontFamily: MONO_FONT, fontWeight: 750 }}>
          {t('scopeEmissions.scope1.volumeSeries')}: {formatLitre(litres?.value || 0, { withUnit: true })}
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: MONO_FONT, fontWeight: 750 }}>
          {t('scopeEmissions.scope1.emissionsSeries')}: {formatTonne(emissions?.value || 0, { withUnit: true })}
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function Scope1TrendChart({ data, color, ariaLabel }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const emissionsColor = theme.palette.mode === 'dark' ? '#e879f9' : '#a21caf';
  const chartData = data.map((item) => ({
    ...item,
    month: t(`dashboard.months.${item.monthKey}`),
  }));

  return (
    <Box role="img" aria-label={ariaLabel} sx={{ height: { xs: 310, md: 340 }, minWidth: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={chartData}
          margin={{ top: compact ? 8 : 24, right: compact ? 0 : 12, left: compact ? 0 : 8, bottom: 0 }}
          barCategoryGap={compact ? '18%' : '28%'}
        >
          <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="month"
            axisLine={{ stroke: theme.palette.divider }}
            interval={0}
            tick={{ fill: theme.palette.text.secondary, fontSize: compact ? 9 : 11 }}
            tickLine={false}
          />
          <YAxis
            yAxisId="litres"
            axisLine={false}
            tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
            tickFormatter={compactVolume}
            tickLine={false}
            width={compact ? 38 : 54}
            label={compact ? undefined : { value: 'L', angle: -90, position: 'insideLeft' }}
          />
          <YAxis
            yAxisId="emissions"
            orientation="right"
            axisLine={false}
            tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
            tickFormatter={compactNumber}
            tickLine={false}
            width={compact ? 38 : 54}
            label={compact ? undefined : { value: 'tCO₂e', angle: 90, position: 'insideRight' }}
          />
          <Tooltip cursor={{ fill: theme.palette.action.hover }} content={<CombinedTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar
            yAxisId="litres"
            dataKey="volumeLitres"
            fill={color}
            maxBarSize={52}
            name={t('scopeEmissions.scope1.volumeSeries')}
            radius={[5, 5, 0, 0]}
          >
          </Bar>
          <Line
            yAxisId="emissions"
            dataKey="emissions"
            dot={{
              fill: emissionsColor,
              r: 3.5,
              stroke: theme.palette.background.paper,
              strokeWidth: 1.5,
            }}
            activeDot={{
              fill: emissionsColor,
              r: 5.5,
              stroke: theme.palette.background.paper,
              strokeWidth: 2,
            }}
            name={t('scopeEmissions.scope1.emissionsSeries')}
            stroke={emissionsColor}
            strokeWidth={3}
            type="monotone"
          >
            {!compact && (
              <LabelList
                dataKey="emissions"
                position="top"
                offset={6}
                formatter={formatEmissionLabel}
                style={{ fill: emissionsColor, fontSize: 10, fontWeight: 700 }}
              />
            )}
          </Line>
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
